import { useEffect, useMemo, useState } from "react";
import { fetchMowerStatuses, MowerStatus } from "../lib/api";
import { getDeviceScope } from "../lib/scope";
import { fetchDeviceCustomerIndex, type DeviceCustomerIndex } from "../lib/users";
import { FleetMap } from "../components/FleetMap";
import { useDashboardSocket, useLiveFleet } from "../live/DashboardSocketContext";
import type { MergedMowerState } from "../lib/liveFrames";
import { LiveIndicator } from "../components/LiveIndicator";
import { isValidSoc, socLabel } from "../lib/telemetryReadings";

type Status = "mowing" | "standby" | "attention" | "fault" | "offline";

const SC: Record<Status, string> = {
  mowing: "#22c55e",
  standby: "#3b82f6",
  attention: "#f59e0b",
  fault: "#ef4444",
  offline: "#888",
};
const SL: Record<Status, string> = {
  mowing: "Mowing",
  standby: "Standby",
  attention: "Attention needed",
  fault: "Fault",
  offline: "Offline",
};

function classify(m: MowerStatus, live?: MergedMowerState): Status {
  // `connected` is delta-sourced and only meaningful once a robot_state frame
  // has arrived — `undefined` means unknown, which is NOT the same as offline,
  // so fall through to the timestamp heuristic in that case.
  if (live?.connected === false) return "offline";
  const now = Date.now();
  const last = new Date(m.last_update).getTime();
  const hoursSince = (now - last) / 1000 / 3600;
  // A live connection is direct evidence of reachability and outranks a stale
  // last_update (which only advances when telemetry is persisted).
  if (hoursSince > 24 && live?.connected !== true) return "offline";
  if (!m.is_ready_for_autonomy) return "fault";
  if (m.status_message && /attention|warn/i.test(m.status_message))
    return "attention";
  if (m.status_message && /mow/i.test(m.status_message)) return "mowing";
  return "standby";
}

/** Shown in the Customer column for devices in nobody's pool. */
const NO_CUSTOMER = "Unassigned";

function formatLastActive(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  if (isToday) return `Today, ${time}`;
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${time}`;
}

export function FleetPage({
  onOpenSessions,
}: {
  /** Navigate to the Sessions tab pre-filtered to this mower. */
  onOpenSessions?: (deviceId: string) => void;
}) {
  const [mowers, setMowers] = useState<MowerStatus[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // null = caller can't see customer assignments (external user) → hide column.
  const [customerIndex, setCustomerIndex] = useState<DeviceCustomerIndex | null>(null);
  const [customer, setCustomer] = useState<string>("all");
  const [status, setStatus] = useState<Status | "all">("all");

  const { connected, sessionRefetch } = useDashboardSocket();
  const fleet = useLiveFleet();

  const [reloadKey, setReloadKey] = useState(0);
  // A `session` delta means the durable row in SQL changed — refetch it rather
  // than painting the delta, which carries no fleet state (ADR-0007 D4). Fires
  // only on a real bump, never on mount.
  useEffect(() => {
    if (sessionRefetch.seq === 0) return;
    setReloadKey((k) => k + 1);
  }, [sessionRefetch]);

  useEffect(() => {
    let cancelled = false;
    // Only the first load blanks the page; a live-triggered refetch swaps rows
    // underneath the user without flashing a loading state at them.
    setError(null);
    (async () => {
      try {
        const scope = await getDeviceScope();
        // /mower-status is unscoped server-side, so restrict client-side to the
        // customer's devices. `null` = internal staff (see all).
        const all = await fetchMowerStatuses();
        const filtered =
          scope === null
            ? all
            : all.filter((m) => scope.includes(m.device_id));
        if (cancelled) return;
        setMowers(filtered);
        setLoading(false);
        // Customer assignments are staff-only, secondary, and cost one request
        // per customer — fetch them after the fleet is on screen, and fall back
        // to "no customer column" if they're unavailable.
        if (scope === null) {
          const idx = await fetchDeviceCustomerIndex().catch(() => null);
          if (!cancelled) setCustomerIndex(idx);
        }
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const showCustomer = customerIndex != null;

  // REST establishes which devices exist (and applies the customer scope); the
  // socket then overlays whatever it knows about them. Deliberately not the
  // other way round: the snapshot is authorization-scoped server-side, but the
  // customer-pool scoping Command applies is its own, so the REST list stays
  // the gate on what a manager may see.
  const rows = useMemo(
    () =>
      mowers.map((m) => {
        const live = fleet[m.device_id];
        const merged: MowerStatus = live
          ? {
              ...m,
              // Only fields the live side actually carries — a delta that omits
              // a field must never blank the REST value.
              ...(live.latitude != null ? { latitude: live.latitude } : {}),
              ...(live.longitude != null ? { longitude: live.longitude } : {}),
              ...(live.has_gps_lock != null
                ? { has_gps_lock: live.has_gps_lock }
                : {}),
              ...(live.battery_charge_percent != null
                ? { battery_charge_percent: live.battery_charge_percent }
                : {}),
              ...(live.status_message
                ? { status_message: live.status_message }
                : {}),
              ...(live.is_ready_for_autonomy != null
                ? { is_ready_for_autonomy: live.is_ready_for_autonomy }
                : {}),
              ...(live.last_update ? { last_update: live.last_update } : {}),
            }
          : m;
        return {
          ...merged,
          status: classify(merged, live),
          live: live?.connected === true,
          customer: customerIndex?.byDevice[m.device_id] ?? NO_CUSTOMER,
        };
      }),
    [mowers, customerIndex, fleet],
  );

  // Offer every customer that owns a device *in this fleet view*, plus
  // "Unassigned" when some unit has no owner.
  const customerOptions = useMemo(() => {
    const present = new Set(rows.map((r) => r.customer));
    present.delete(NO_CUSTOMER);
    const list = Array.from(present).sort((a, b) => a.localeCompare(b));
    if (rows.some((r) => r.customer === NO_CUSTOMER)) list.push(NO_CUSTOMER);
    return list;
  }, [rows]);

  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          (status === "all" || r.status === status) &&
          (!showCustomer || customer === "all" || r.customer === customer),
      ),
    [rows, status, customer, showCustomer],
  );

  const mapUnits = useMemo(
    () =>
      visible.map((m) => ({
        device_id: m.device_id,
        latitude: m.latitude,
        longitude: m.longitude,
        has_gps_lock: m.has_gps_lock,
        status: m.status,
        statusLabel: SL[m.status],
        color: SC[m.status],
        battery_charge_percent: m.battery_charge_percent ?? null,
      })),
    [visible],
  );

  if (loading)
    return <p style={{ color: "var(--color-text-secondary)" }}>Loading fleet…</p>;
  if (error)
    return (
      <p style={{ color: "var(--color-text-danger)" }}>
        Couldn't load fleet: {error}
      </p>
    );

  return (
    <>
      <div className="cd">
        <div className="card-head-row">
          <p className="ct" style={{ margin: 0 }}>FLEET LOCATIONS</p>
          <LiveIndicator connected={connected} />
        </div>
        <div className="mapp">
          <FleetMap units={mapUnits} onSelect={onOpenSessions} />
        </div>
        <p style={{ fontSize: 10, color: "var(--color-text-tertiary)", margin: "6px 0 0" }}>
          Live positions for units reporting a GPS fix. Color = current status.
          {visible.length !== rows.length && " Showing filtered units only."}
          {!connected &&
            " Live channel is down — positions are from the last load."}
        </p>
      </div>
      <div className="cd" style={{ marginBottom: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <p className="ct" style={{ margin: 0 }}>
            ALL UNITS — {visible.length}
            {visible.length !== rows.length ? ` OF ${rows.length}` : " TOTAL"}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {showCustomer && (
              <label className="ss-filter">
                <span>Customer</span>
                <select value={customer} onChange={(e) => setCustomer(e.target.value)}>
                  <option value="all">All customers</option>
                  {/* Keep the active pick listed even if nothing matches it now,
                      so the filter doesn't silently reset itself. */}
                  {(customerOptions.includes(customer) || customer === "all"
                    ? customerOptions
                    : [customer, ...customerOptions]
                  ).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="ss-filter">
              <span>Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as Status | "all")}
              >
                <option value="all">All statuses</option>
                {(Object.keys(SC) as Status[]).map((s) => (
                  <option key={s} value={s}>
                    {SL[s]}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
          {(Object.keys(SC) as Status[]).map((s) => {
            const n = rows.filter((r) => r.status === s).length;
            const on = status === s;
            return (
              <button
                key={s}
                type="button"
                className={"tip-pill status-pill" + (on ? " on" : "")}
                aria-pressed={on}
                onClick={() => setStatus(on ? "all" : s)}
                title={on ? "Clear status filter" : `Show only ${SL[s]}`}
              >
                <span className="dot" style={{ background: SC[s] }} />
                {SL[s]}
                <span className="status-pill-n">{n}</span>
              </button>
            );
          })}
        </div>
        <table className="ft">
          <thead>
            <tr>
              <th style={{ width: 120 }}>UNIT</th>
              {showCustomer && <th style={{ width: 170 }}>CUSTOMER</th>}
              <th style={{ width: 150 }}>STATUS</th>
              <th style={{ width: 100 }}>BATTERY</th>
              <th>LAST ACTIVE</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td
                  colSpan={showCustomer ? 5 : 4}
                  style={{ color: "var(--color-text-tertiary)", fontSize: 11 }}
                >
                  No units match these filters.
                </td>
              </tr>
            )}
            {visible.map((m) => (
              <tr
                key={m.device_id}
                onClick={onOpenSessions ? () => onOpenSessions(m.device_id) : undefined}
                style={{ cursor: onOpenSessions ? "pointer" : undefined }}
                title={onOpenSessions ? `View sessions for ${m.device_id}` : undefined}
              >
                <td style={{ fontWeight: 700 }}>
                  {m.device_id}
                  {onOpenSessions && (
                    <i
                      className="ti ti-chevron-right"
                      style={{ color: "var(--color-text-tertiary)", marginLeft: 4, fontSize: 12, verticalAlign: "middle" }}
                      aria-hidden="true"
                    />
                  )}
                </td>
                {showCustomer && (
                  <td
                    style={{
                      fontSize: 11,
                      color:
                        m.customer === NO_CUSTOMER
                          ? "var(--color-text-tertiary)"
                          : "var(--color-text-secondary)",
                      fontStyle: m.customer === NO_CUSTOMER ? "italic" : undefined,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={m.customer}
                  >
                    {m.customer}
                  </td>
                )}
                <td style={{ fontSize: 11 }}>
                  <span className="dot" style={{ background: SC[m.status] }} />
                  {SL[m.status]}
                </td>
                {/* "Disconnected" is a state, not a measurement — it drops the
                    numeric face so it doesn't read as a value in the column. */}
                <td
                  style={
                    isValidSoc(m.battery_charge_percent)
                      ? { fontFamily: "'Roboto Slab', Georgia, serif", fontWeight: 700 }
                      : { fontSize: 11, color: "var(--color-text-tertiary)", fontStyle: "italic" }
                  }
                  title={
                    isValidSoc(m.battery_charge_percent)
                      ? undefined
                      : "The mower isn't reporting a battery reading."
                  }
                >
                  {socLabel(m.battery_charge_percent)}
                </td>
                <td style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                  {formatLastActive(m.last_update)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
